/**
 * WordPress dependencies
 */
import type * as wpData from '@safe-wordpress/data';
type WpData = typeof wpData;

/**
 * External dependencies
 */
import type { Dict } from '@nab/types';

/**
 * Internal dependencies
 */
import { getDocumentHeight as _getDocumentHeight } from '../../../../public/utils/helpers/get-document-height';

const MAX_DESKTOP_HEIGHT = 100_000;

export function createElement< T extends HTMLElement = HTMLElement >(
	type: string,
	options: Dict & { style?: Dict< string > } = {}
): T {
	const el = document.createElement( type );

	const { id, className, style, ...props } = options;
	if ( 'string' === typeof id ) {
		el.id = id;
	}

	if ( 'string' === typeof className ) {
		el.className = className;
	}

	if ( 'object' === typeof style ) {
		Object.keys( style ).forEach( ( k ) => {
			( el as unknown as { style: Dict< string > } ).style[ k ] =
				style[ k ] ?? '';
		} );
	}

	Object.keys( props ).forEach( ( p ) => {
		( el as unknown as Dict )[ p ] = props[ p ];
	} );

	return el as T;
}

export const getDocumentHeight = (): number => {
	const pairs = Array.from(
		document.querySelectorAll( '.nab-overlay-element' )
	)
		.map( ( el ) => [ el.parentElement, el ] )
		.filter(
			( pair ): pair is [ HTMLElement, HTMLElement ] => !! pair[ 0 ]
		);

	pairs.forEach( ( [ p, c ] ) => p.removeChild( c ) );
	const height = Math.min( _getDocumentHeight(), MAX_DESKTOP_HEIGHT );
	pairs.forEach( ( [ p, c ] ) => p.appendChild( c ) );
	return height;
};

export function createOverlayElement< T extends HTMLElement = HTMLElement >(
	type: string
): T {
	const el = document.createElement( type );
	el.className = 'nab-overlay-element';
	el.style.position = 'absolute';
	el.style.left = '0px';
	el.style.top = '0px';
	el.style.zIndex = '999999';
	maximizeElement( el );

	return el as T;
}

export function maximizeElement( el: HTMLElement ): void {
	const width = document.body.clientWidth;
	const height = getDocumentHeight();

	el.style.width = `${ width }px`;
	el.style.height = `${ height }px`;

	if ( isHTMLCanvas( el ) ) {
		el.width = width;
		el.height = height;
	}
}

export const hasWordPressData = (
	win: unknown
): win is {
	readonly wp: {
		readonly data: WpData;
	};
} =>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
	!! ( win as any )?.wp?.data;

const isHTMLCanvas = ( el: HTMLElement ): el is HTMLCanvasElement =>
	'canvas' === el.nodeName.toLowerCase();
